import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AccessibleOfficeDto } from '@gh/builder-shared-data';
import { TranslationService } from '@gh/core-mls';
import { DialogInput, STYLES_DIALOG_LAYOUT } from '@gh/core-ui';

import { MAX_OFFICE_CARDS } from '../select-office-dialog/select-office-dialog.component';

export type SwitchOfficeDialogConfig = {
  offices: AccessibleOfficeDto[];
  currentOfficeId?: string;
};

@Component({
  standalone: false,
  selector: 'gh-switch-office-dialog',
  templateUrl: 'switch-office-dialog.component.html',
  styleUrls: ['switch-office-dialog.component.scss'],
  styles: [STYLES_DIALOG_LAYOUT],
})
export class SwitchOfficeDialogComponent implements DialogInput<SwitchOfficeDialogConfig> {
  selectedOffice?: AccessibleOfficeDto;
  private _input: SwitchOfficeDialogConfig;

  readonly title = this.resolveLabel('lbl_menu_switch_office', 'Switch Office');
  readonly intro = this.resolveLabel(
    'msg_select_office_to_continue',
    'Choose the office you want to work in for this session.',
  );
  readonly officeLabel = this.resolveLabel('lbl_office', 'Office');
  readonly homeOfficeLabel = this.resolveLabel('lbl_primary_office', 'Primary Office');
  readonly currentLabel = this.resolveLabel('lbl_current_office', 'Current');
  readonly continueLabel = this.resolveLabel('lbl_continue', 'Continue');

  constructor(
    private dialogRef: MatDialogRef<SwitchOfficeDialogComponent>,
    private translation: TranslationService,
  ) {}

  set input(value: SwitchOfficeDialogConfig) {
    this._input = value;
    // Do not pre-select an office — user must choose explicitly.
    this.selectedOffice = undefined;
  }

  get input(): SwitchOfficeDialogConfig {
    return this._input;
  }

  get offices(): AccessibleOfficeDto[] {
    return this._input?.offices || [];
  }

  /** Same rule as the login picker: more than MAX_OFFICE_CARDS offices switch the list to a dropdown. */
  get useDropdown(): boolean {
    return this.offices.length > MAX_OFFICE_CARDS;
  }

  compareOffices = (left: Maybe<AccessibleOfficeDto>, right: Maybe<AccessibleOfficeDto>): boolean =>
    Boolean(left && right) && String(left!.officeId) === String(right!.officeId);

  isCurrent(office: AccessibleOfficeDto): boolean {
    const currentId = this._input?.currentOfficeId;
    return Boolean(currentId) && String(office.officeId) === String(currentId);
  }

  isSelected(office: AccessibleOfficeDto): boolean {
    const selectedId = this.selectedOffice?.officeId;
    return Boolean(selectedId) && String(selectedId) === String(office.officeId);
  }

  selectOffice(office: AccessibleOfficeDto): void {
    if (this.isCurrent(office)) {
      return;
    }
    this.selectedOffice = office;
  }

  selectOfficeById(officeId: unknown): void {
    const office = this.offices.find((candidate) => String(candidate.officeId) === String(officeId));
    if (office) {
      this.selectOffice(office);
    }
  }

  onContinue(): void {
    if (!this.selectedOffice || this.isCurrent(this.selectedOffice)) {
      return;
    }
    this.dialogRef.close(this.selectedOffice);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private resolveLabel(key: string, fallback: string): string {
    const translated = this.translation.get(key);
    return !translated || translated === key ? fallback : translated;
  }
}
